//@import dependencies
import React, { useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { AiFillSafetyCertificate, AiOutlineFolderView, AiOutlineUpload } from 'react-icons/ai'
import { FaFilePdf, FaRegFilePdf } from 'react-icons/fa'
//@end

//@import components
import BreadcrumbComponent from 'app_components/commons/breadcrumb'
import DataTableComponent from 'app_components/commons/dataTable'
import AppContainer from 'app_components/layouts/app'
import BulkUploadModalModule from 'app_components/modules/admin/bulkUploadModal'
import ModalGenerateCertificationsModule from 'app_components/modules/modalGenerateCertifications'
// import CourseSchedulingDetailsForm from 'app_components/modules/admin/courseScheduling/details/courseSchedulingDetailsForm'
import CourseSchedulingEnrollmentSkeleton from './courseSchedulingEnrollmentSkeleton'
//@end

// @import hooks
import { useColors } from 'app_hooks/useColors'
// @end

// @INFO Import types
import { CertificateCategory, ICertificate, ICertificateStage, ICertificationsStage, ICourseSchedulingEnrollmentListItem } from 'app_types/admin/courseScheduling/courseSchedulingEnrollmentTypes'
import { ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes'
import { IPagination } from 'app_types/pagination/paginationTypes'
// @end

// @INFO Import services
import { I18nUtil } from 'app_utils/i18n'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import CertificateService from 'app_services/user/certificateService'
import UserService from 'app_services/user/userService'
// @end

// @INFO Import Styles
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import SavingIndicator, { TypesStateSavingIndicator } from 'app_components/commons/SavingIndicator/SavingIndicator'
import moment from 'moment'
import './../courseSchedulingEnrollmentScreen.scss'
import { IoIosRefreshCircle } from 'react-icons/io'
import ModalCertificationStage from 'app_components/modules/modalCertificationStage/modalCertificationStage'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent'
import { FiAlertTriangle } from 'react-icons/fi'
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent'
import CertificationControlWrapper from '../../../../commons/CertificationControlWrapper/CertificationControlWrapper';
import { BsCardList } from 'react-icons/bs'
import ModalGenerateMultipleCertificates from './components/ModalGenerateMultipleCertificates/ModalGenerateMultipleCertificates';
import { MdError, MdOutlineDelete } from 'react-icons/md'
import ModalEnrollmentCertificates from './components/ModalEnrollmentCertificates/ModalEnrollmentCertificates'
import { BounceLoader } from 'react-spinners'

const HELPER_FILE_URL = 'https://campus.icontecvirtual.edu.co/uploads/students-template/format-massive.xlsx'

// @ADD Component types
interface ICourseSchedulingEnrollmentScreenLayoutProps {
  courseScheduling: ICourseSchedulingListItem | null
  itemList: ICourseSchedulingEnrollmentListItem[]
  status: null | 'loading' | 'error'
  add: boolean
  edit: boolean
  view: boolean
  editData: any
  selectedRow: null | string
  pagination: IPagination
  addItem: any
  updateItem: any
  canViewCertifications: boolean
  massiveUpload: () => void
  certificationGenerated: () => void | undefined
  showAddForm: (add: boolean) => void
  showEditForm: (edit: boolean, editData?: any) => void
  onViewerRow: (view: boolean, editData?: any) => void
  onPageChange: (page: number) => void
  onRowClick: (row: any) => void
  removeItem: (userId: string) => void
  onSearch: (search: string) => void
  updateLoadParticipantsStatus: (status: boolean) => void
  reGenerateCertifications: () => void
}

const CourseSchedulingEnrollmentScreenLayout: React.FC<ICourseSchedulingEnrollmentScreenLayoutProps> = (props) => {

  const i18nUtil = new I18nUtil()
  const screenNotifications = new ScreenNotificationsUtil()
  const userService: UserService = new UserService()
  const certificateService: CertificateService = new CertificateService()
  const { t } = useTranslation()
  const [pdfDownload, setPdfDownload] = useState<{
    status: 'initial' | 'loaded' | 'loading',
    file: any | null,
  }>({ status: 'initial', file: null })

  const [colors] = useColors()

  const [isCheckAll, setIsCheckAll] = useState<boolean>(false)
  const [isCheck, setIsCheck] = useState<Array<any>>([])
  const [certificationStageModal, updateCertificationStageModal] = useState<ICertificateStage>({status: false})
  const [uploadBulkFormStatus, setUploadBulkFormStatus] = useState<boolean>(false)
  const [generateCertificationsStatus, setGenerateCertifications] = useState<boolean>(false)
  const [showCertificatesUserModal, setShowCertificatesUserModal] = useState<ICourseSchedulingEnrollmentListItem | null>(null)

  const handleUploadBulkModal = () => {
    setUploadBulkFormStatus(true)
  }

  const handleCloseUploadBulkModal = (data: any) => {
    setUploadBulkFormStatus(false)
    if (data?.status === true) {
      props.massiveUpload()
      if (props?.courseScheduling) {
        props.updateLoadParticipantsStatus(true)
      }
    }
  }

  const openGenerateCertificationsModal = () => {
    setGenerateCertifications(true)
  }

  const handleCloseGenerateCertificationModal = (data: any) => {
    if (props.certificationGenerated) {
      setGenerateCertifications(false)
      if (data?.status === 'success') {
        props.certificationGenerated()
      }
    }
  }

  const handleDownloadFile = async (status: boolean, certificate: ICertificate) => {
    if (status === true && certificate) {
      setPdfDownload({ status: 'loading', file: null })
      if (certificate?.certificate?.pdfPath) {
        setPdfDownload({ status: 'loaded', file: certificate?.certificate?.pdfPath })
        window.open(certificate?.certificate?.pdfPath, '_blank')
      } else {
        const responsePreview = await certificateService.loadPreview(certificate?.certificate?.hash, {
          format: 2,
          template: 1,
          updateCertificate: true,
          certificate_queue: certificate._id,
        })
        if (responsePreview && responsePreview.certificate?.pdfPath) {
          setPdfDownload({ status: 'loaded', file: responsePreview.certificate?.pdfPath })
          certificate.certificate.pdfPath = responsePreview.certificate?.pdfPath
          window.open(responsePreview.certificate?.pdfPath, '_blank')
        } else {
          setPdfDownload({ status: 'initial', file: null })
        }
      }
    } else {
      if (certificate.status === 'Requested')
        setPdfDownload({ status: 'loading', file: null })
      else
        setPdfDownload({ status: 'initial', file: null })
    }
  }

  const certificationStructure = (certificate: ICertificate) => {
    let title = `${t('screens.course_scheduling.certifications.generating_certificate')} - ${certificate.status}`
    let icon = <Spinner className='spinner-loading' animation='border' size='sm' />
    let component = (
      <button
        className='btn btn-link btn-sm'
        data-tip={title}
      >
        {icon}
      </button>
    )
    //enrollment.certificate[0]
    if (certificate.status === 'Complete') {
      title = t('screens.course_scheduling.certifications.download_certificate')
      icon = <FaFilePdf size='20px' color={colors.primary} />
      component = (
        <button
          className='btn btn-link btn-sm'
          data-tip={title}
          onClick={() => handleDownloadFile(true, certificate)}
          disabled={pdfDownload.status === 'loading'}
        >
          {pdfDownload.status === 'loading' ? (
            <Spinner className='spinner-loading' animation='border' size='sm' />
          ) : (
            <FaFilePdf size='20px' color={colors.primary} />
          )}
        </button>
      )
    } else if (certificate.status === 'Error') {
      title = `${t('screens.course_scheduling.certifications.certificate_error')}: ${certificate.errorMessage}`
      icon = <MdError size='20px' color={'red'} />
      component = (
        <button
          className='btn btn-link btn-sm'
          data-tip={title}
        >
          {icon}
        </button>
      )
    }

    return component
  }

  const secondCertificationStructure = (certificate: ICertificate) => {
    let title = `${t('screens.course_scheduling.certifications.generating_certificate_2')} - ${certificate.status}`
    let icon = <Spinner className='spinner-loading' animation='border' size='sm' />
    let component = (
      <button
        className='btn btn-link btn-sm'
        data-tip={title}
      >
        {icon}
      </button>
    )
    //enrollment.certificate[1]
    if (certificate.status === 'Complete') {
      title = t('screens.course_scheduling.certifications.download_certificate_2')
      icon = <FaRegFilePdf size='20px' color={colors.primary} />
      component = (
        <button
          className='btn btn-link btn-sm'
          data-tip={title}
          onClick={() => handleDownloadFile(true, certificate)}
          disabled={pdfDownload.status === 'loading'}
        >
          {pdfDownload.status === 'loading' ? (
            <Spinner className='spinner-loading' animation='border' size='sm' />
          ) : (
            <FaRegFilePdf size='20px' color={colors.primary} />
          )}
        </button>
      )
    } else if (certificate.status === 'Error') {
      title = `${t('screens.course_scheduling.certifications.certificate_error')} auditor: ${certificate.errorMessage}`
      icon = <MdError size='20px' color={'red'} />
      component = (
        <button
          className='btn btn-link btn-sm'
          data-tip={title}
        >
          {icon}
        </button>
      )
    }

    return component
  }

  const handlereGenerateCertificate = async (certificate: ICertificate) => {
    let confirm = screenNotifications.fire({
      message: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.message')}`,
    },
    {
      type: 'warning',
      title: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.title')}`,
      confirmButtonText: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.confirmButtonText')}`,
      cancelButtonText: `${i18nUtil.getTranslate('components.screen_notifications.buttons.cancelButtonText')}`,
      showCancelButton: true,
    })

    if (confirm) {
      confirm.then(async (result) => {
        if (result.value) {
          screenNotifications.fireProcessing()

          const response = await certificateService.reGenerateCertification({
            courseId: certificate.courseId,
            userId: certificate.userId
          })

          if (response) {
            screenNotifications.fireToast(
              {
                message: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.success')}`,
              },
              {
                position: 'top',
              },
            )
            props.reGenerateCertifications()
          }
        }
      })
    }
  }

  const certificationReIssue = (certificate: ICertificate) => {
    let title = `${t('screens.course_scheduling.certifications.re_issue.loading')}`
    let icon = <Spinner className='spinner-loading' animation='border' size='sm' />
    let component = (
      <button
        className='btn btn-link btn-sm'
        data-tip={title}
      >
        {icon}
      </button>
    )
    if (certificate.status === 'Complete') {
      title = t('screens.course_scheduling.certifications.re_issue.title')
      icon = <IoIosRefreshCircle size='20px' color={colors.primary} />
      component = (
        <button
          className='btn btn-link btn-sm'
          data-tip={title}
          onClick={() => handlereGenerateCertificate(certificate)}
        >
          {icon}
        </button>
      )
    }

    return component
  }

  const handleSelectAll = (e: any) => {
    setIsCheckAll(!isCheckAll)
    setIsCheck(props.itemList.map(li => `certification_${li._id}`))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleCheckClick = (e: any) => {
    const { id, checked } = e.target
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter(item => item !== id))
    }
  }

  const openCertificationStageModal = (category: CertificateCategory, title: string, message: string, notes: string) => {
    const certifications = props.itemList.reduce((accum: ICertificationsStage[], element: ICourseSchedulingEnrollmentListItem) => {
      if (element?.certificate) {
        if (isCheck.includes(`certification_${element._id?.toString()}`)) {
          element?.certificate.forEach((certificate) => {
            let available = true;
            switch (category) {
              case CertificateCategory.FORCE_PREVIEW:
                if (!['Complete'].includes(certificate.status)) {
                  available = false
                }
                break;
              case CertificateCategory.FORCE_PROCESS:
                if (['Complete'].includes(certificate.status)) {
                  available = false
                }
                break;
            }
            if (available) {
              accum.push({
                certificationQueueId: certificate._id,
                certificationCode: element.enrollmentCode,
                certificationStatus: certificate.status,
                certificateConsecutive: certificate.certificateConsecutive,
                userDocNumber: element?.user?.profile?.doc_number,
                userFullName: element?.user?.profile ? `${element?.user?.profile?.first_name} ${element?.user?.profile?.last_name}` : '-',
                certificationType: certificate.certificateType
              })
            }
          })
        }
      }
      return accum;
    }, [])
    updateCertificationStageModal({
      status: true,
      certifications,
      title,
      message,
      notes,
      category
    })
  }

  const handleCloseCertificationStageModal = (data: any) => {
    updateCertificationStageModal({
      status: false,
      certifications: [],
      title: undefined,
      message: undefined,
      notes: undefined,
      category: undefined
    })
    if (data?.status === true) {
      window.location.reload()
    }
  }

  const showCertificatesInfo = (enrollment: ICourseSchedulingEnrollmentListItem) => {
    if (enrollment.certificate) {
      const certificates = enrollment.certificate.filter((item) => item?.certificateSetting)
      if (certificates?.length === 0) {
        return;
      } else {
        const amountCertificates = certificates?.length
        const {completed, processing} = certificates?.reduce((accum, certificate) => {
          if (certificate.status === 'Complete') {
            accum.completed += 1;
          } else {
            accum.processing += 1;
          }
          return accum;
        }, {completed: 0, processing: 0})
        let tooltipMessage = ''
        let state: TypesStateSavingIndicator;
        let iconColor = ''
        if (processing > 0) {
          tooltipMessage = `Se han procesado completamente ${completed} certificados y aún están en proceso de generación ${processing} certificados.`
          state = 'saving'
          iconColor = 'var(--orange)'
        } else {
          tooltipMessage = `Se han procesado completamente ${completed} certificados de un total de ${amountCertificates} generados.`
          state = 'saved'
          iconColor = 'var(--green)'
        }
        return (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <TooltipComponent
              text={tooltipMessage}
            >
              <SavingIndicator
                state={state}
                savingIcon={(
                  <BounceLoader
                    color={iconColor}
                    size={10}
                  />
                )}
              />
            </TooltipComponent>
          </div>
        )
      }
    }

  }

  return (
    <AppContainer>
      <div className='course_scheduling_enrollment-component container-fluid'>
        <BreadcrumbComponent
          links={[
            '/admin/course-scheduling',
            '#',
          ]}
          texts={[
            `${t('components.breadcrumb.course_scheduling')}`,
            `${t('components.breadcrumb.course_scheduling_enrollment')}`,
          ]} />

        <div className='course-scheduling-enrollment-screen'>

          {props.status === 'loading' && (
            <CourseSchedulingEnrollmentSkeleton />
          )}

          {props.status !== 'loading' && !props.add && !props.edit && !props.view && (
            <>
              <h2>{t('screens.course_scheduling.enrollment.title')}</h2>
              <h3>{props.courseScheduling?.program.name}</h3>
              <h4>{`${t('screens.course_scheduling.enrollment.service_id')}: ${props.courseScheduling?.metadata.service_id}`}</h4>

              <div className='enrollment-screen--upload-container'>
                {props?.courseScheduling?.loadParticipants?.loading !== undefined ?
                  <SavingIndicator
                    state={props?.courseScheduling?.loadParticipants?.loading ? 'saving' : (props?.courseScheduling?.loadParticipants?.status === 'success' ? 'saved' : 'failed')}
                    withText
                    savingText='Procesando...'
                    failedText='Error al cargar estudiantes'
                    savedText='Estudiantes matriculados con éxito'
                    descriptionText={props?.courseScheduling?.loadParticipants?.lastUploadDate ? `Última actualización: ${moment(props?.courseScheduling?.loadParticipants?.lastUploadDate).format('YYYY-MM-DD')}` : undefined}
                    details={props?.courseScheduling?.loadParticipants.errors}
                  />
                :null}
                {(props?.courseScheduling?.schedulingStatus?.name !== 'Cancelado') && (props.addItem || props.updateItem) ?
                  <div className='course-scheduling-enrollment-screen--upload-actions'>
                    <ButtonComponent
                      variant='primary'
                      onClick={handleUploadBulkModal}
                      disabled={props?.courseScheduling?.loadParticipants?.loading}
                    >
                      <ButtonComponent.Icon>
                        <AiOutlineUpload/>
                      </ButtonComponent.Icon>
                      {t('screens.course_scheduling.enrollment.upload_action.title')}
                    </ButtonComponent>
                    <div className='course-scheduling-enrollment-screen--alert'>
                      <TooltipComponent text='Puedes descargarla nuevamente desde el menú de carga de estudiantes'>
                        <FiAlertTriangle />
                      </TooltipComponent>
                      <LabelComponent>La plantilla ha cambiado</LabelComponent>
                    </div>
                  </div>
                : null}
              </div>

              <DataTableComponent
                data={props.itemList}
                headerKeys={[
                  (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '8px',
                      }}
                    >
                      <input
                        type='checkbox'
                        value='check_all'
                        name={`check_all`}
                        id={`check_all`}
                        onChange={handleSelectAll}
                        checked={isCheckAll}
                      />
                    </div>
                  ),
                  `${t('components.dataTable.headers.course_scheduling.enrollment.count')}`,
                  `${t('components.dataTable.headers.course_scheduling.enrollment.doc_type')}`,
                  `${t('components.dataTable.headers.course_scheduling.enrollment.doc_number')}`,
                  `${t('components.dataTable.headers.course_scheduling.enrollment.fullname')}`,
                  `${t('components.dataTable.headers.course_scheduling.enrollment.email')}`,
                  `${t('components.dataTable.headers.course_scheduling.enrollment.phoneNumber')}`,
                  `${t('components.dataTable.headers.course_scheduling.enrollment.regional')}`,
                  `${t('components.dataTable.headers.course_scheduling.enrollment.account_executive')}`,
                ]}
                keys={['enrollmentCode', 'user.profile.doc_type', 'user.profile.doc_number', 'user.fullname', 'user.email', 'user.phoneNumber', 'user.profile.regional', 'origin']}
                selectedRow={props.selectedRow}
                pagination={props.pagination}
                detailsKey={[]}
                firstColRowButtons={(student) => {
                  return (
                    <>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          value={`${student._id}`}
                          id={`certification_${student._id}`}
                          name={`certifications`}
                          onChange={handleCheckClick}
                          checked={isCheck.includes(`certification_${student._id}`)}
                        />
                      </div>
                    </>
                  )
                }}
                onPageChange={props.onPageChange}
                onModal={null}
                headerButtons={() => {
                  return (
                    <>
                      {props?.courseScheduling?.schedulingStatus?.name && ['Confirmado', 'Ejecutado'].includes(props?.courseScheduling?.schedulingStatus.name) && !!props.certificationGenerated
                      && (
                        <button
                          className='btn btn-link btn-sm'
                          data-tip={t('screens.course_scheduling.certifications.generate_certification.title')}
                          onClick={() => openGenerateCertificationsModal()}
                        >
                          <AiFillSafetyCertificate size='30px' color={colors.primary} />
                        </button>
                      )}
                      {userService.can('permission:course_scheduling_enrollment_force_preview_certifications', 'module:course_scheduling_enrollment') ? (
                        <button
                          className='btn btn-link btn-sm'
                          data-tip={t('screens.course_scheduling.certifications.force_process_certification.title')}
                          onClick={() =>
                            openCertificationStageModal(
                              CertificateCategory.FORCE_PROCESS,
                              t('screens.course_scheduling.certifications.force_process_certification.title'),
                              t('screens.course_scheduling.certifications.force_process_certification.message'),
                              '',
                            )}
                        >
                          <AiOutlineFolderView size='30px' color={colors.primary} />
                        </button>
                      ): null}
                    </>
                  )
                }}
                rowButtons={(enrollment: ICourseSchedulingEnrollmentListItem) => {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                      {!!props.courseScheduling && (
                        <CertificationControlWrapper
                          courseScheduling={props.courseScheduling}
                          showOnNormalCertificate
                        >
                          <>
                            {props.canViewCertifications && enrollment.certificate && enrollment.certificate[0] &&
                              ['New', 'In-process', 'Requested', 'Complete', 'Error'].includes(enrollment.certificate[0].status) && (
                              <>
                                {certificationStructure(enrollment.certificate[0])}
                              </>
                            )}
                            {props.canViewCertifications && enrollment.certificate && enrollment.certificate[1] &&
                              ['New', 'In-process', 'Requested', 'Complete', 'Error'].includes(enrollment.certificate[1].status) && (
                              <>
                                {secondCertificationStructure(enrollment.certificate[1])}
                              </>
                            )}
                            {
                              userService.can('permission:course_scheduling_enrollment_reissue_certifications', 'module:course_scheduling_enrollment') &&
                              enrollment.certificate &&
                              enrollment.certificate[0] &&
                              ['Complete', 'Re-issue'].includes(enrollment.certificate[0].status) && (
                              <>
                                {certificationReIssue(enrollment.certificate[0])}
                              </>
                            )}
                          </>
                        </CertificationControlWrapper>
                      )}
                      {!!props.courseScheduling && (
                        <CertificationControlWrapper
                          courseScheduling={props.courseScheduling}
                          showOnMultipleCertificate
                        >
                          <>
                            {showCertificatesInfo(enrollment)}
                            <TooltipComponent text="Ver certificados" >
                              <BsCardList
                                cursor="pointer"
                                color="var(--primary)"
                                onClick={() => setShowCertificatesUserModal(enrollment)}
                                size={20}
                              />
                            </TooltipComponent>
                          </>
                        </CertificationControlWrapper>
                      )}
                      {props?.courseScheduling?.schedulingStatus?.name !== 'Cancelado' && userService.can('permission:course_scheduling_enrollment_delete', 'module:course_scheduling_enrollment') && (
                        <TooltipComponent text="Eliminar usuario">
                          <MdOutlineDelete
                            onClick={() => props.removeItem(enrollment?._id as string)}
                            size={22}
                            cursor="pointer"
                            color="var(--primary)"
                          />
                        </TooltipComponent>
                      )}
                    </div>
                  )
                }}
              />
            </>
          )}

          {uploadBulkFormStatus && (
            <BulkUploadModalModule
              open={uploadBulkFormStatus}
              handleClose={handleCloseUploadBulkModal}
              header={{
                title: t('screens.course_scheduling.enrollment.bulk_upload.title'),
                closeButton: true,
              }}
              endpoint={{
                method: 'POST',
                url: '/api/enrollment/massive',
                params: {
                  courseID: props.courseScheduling?.moodle_id,
                  courseScheduling: props.courseScheduling?._id,
                  sendEmail: true,
                },
              }}
              showErrors={true}
              helperFile={HELPER_FILE_URL}
            />
          )}

          {generateCertificationsStatus && props.courseScheduling?.moodle_id && props.courseScheduling && (
            <>
              <CertificationControlWrapper
                courseScheduling={props.courseScheduling}
                showOnNormalCertificate
              >
                <ModalGenerateCertificationsModule
                  open={generateCertificationsStatus}
                  handleClose={handleCloseGenerateCertificationModal}
                  moodle_id={props.courseScheduling?.moodle_id}
                  course_scheduling={props.courseScheduling}
                />
              </CertificationControlWrapper>
              <CertificationControlWrapper
                courseScheduling={props.courseScheduling}
                showOnMultipleCertificate
              >
                <ModalGenerateMultipleCertificates
                  open={generateCertificationsStatus}
                  onClose={handleCloseGenerateCertificationModal}
                  courseScheduling={props.courseScheduling}
                  // moodle_id={props.courseScheduling?.moodle_id}
                />
              </CertificationControlWrapper>
            </>
          )}

          {certificationStageModal?.status === true && (
            <ModalCertificationStage
              open={certificationStageModal.status}
              handleClose={handleCloseCertificationStageModal}
              certifications={certificationStageModal.certifications || []}
              title={certificationStageModal.title || ''}
              message={certificationStageModal.message || ''}
              notes={certificationStageModal.notes || undefined}
              category={certificationStageModal.category || undefined}
            />
          )}
          {!!props.courseScheduling && !!showCertificatesUserModal && (
            <CertificationControlWrapper
              courseScheduling={props.courseScheduling}
              showOnMultipleCertificate
            >
              <ModalEnrollmentCertificates
                open={!!showCertificatesUserModal}
                onClose={() => setShowCertificatesUserModal(null)}
                enrollment={showCertificatesUserModal}
                courseScheduling={props.courseScheduling}
              />
            </CertificationControlWrapper>
          )}
        </div>
      </div>
    </AppContainer>
  )
}

export default CourseSchedulingEnrollmentScreenLayout
